import * as React from 'react';
import S from './Header.styles';
import SkipLink from '@stories/Components/Misc/SkipLink/SkipLink';
import SvgLogo from '@img/logo.svg';
import SvgArrow from '@img/icons/arrow-right-thin.svg';
import SvgChevron from '@img/icons/chevron.svg';
import { getSystemIcon, SystemIcon } from '@helpers/icons';
import { LinkButton } from '@stories/Components/Buttons/Button/Button';
import MobileNavigation from './MobileNavigation/MobileNavigation';
import useMedia from '@hooks/useMedia';
import { Device, until } from '@helpers/media';
import UspBar from '@stories/Widgets/UspBar/UspBar';
import { clamp } from '@helpers/utils';
const DesktopNavigation = ({ active, links, searchTerm, translations, setSearchTerm, setActive, handleRedirect }) => {
    const mainNavRef = React.useRef(null);
    const navItemRef = React.useRef([]);
    const navItemThirdLvlRef = React.useRef([]);
    const isMobile = useMedia([until(Device.TabletLarge)], [true], false);
    const [thirdLvlItem, setThirdLvlItem] = React.useState('');
    const featuredWidth = 941;
    const childWidth = 809;
    const checkGap = (ref, link) => {
        if (mainNavRef.current && ref) {
            let dropdownAlign = 'left';
            const hasChildrenInChildren = link.children?.some(child => child.children && child.children.length > 0);
            const mainNavRect = mainNavRef.current?.getBoundingClientRect().width;
            const navItemRect = ref.getBoundingClientRect();
            const gap = mainNavRect - navItemRect.right + mainNavRef.current?.getBoundingClientRect().x;
            if (link.featured && link.featured.length > 0) {
                dropdownAlign = gap > featuredWidth ? 'left' : 'right';
            }
            else if (hasChildrenInChildren) {
                dropdownAlign = gap > childWidth ? 'left' : 'right';
            }
            return dropdownAlign;
        }
        else {
            return 'left';
        }
    };
    return (React.createElement(S.Nav, { ref: mainNavRef, role: "menubar", onMouseLeave: () => {
            if (isMobile)
                return;
            setActive(-1);
        } },
        React.createElement(React.Fragment, null,
            links?.map((link, i) => (React.createElement(S.NavItem, { key: link.id, "aria-expanded": active === i },
                React.createElement(S.NavLink, { href: link.url, role: "menuitem", ref: (el) => (navItemRef.current[i] = el), onMouseEnter: () => {
                        if (isMobile) {
                            return;
                        }
                        setActive(i);
                    }, onFocus: () => {
                        if (isMobile) {
                            return;
                        }
                        setActive(i);
                    }, onKeyDown: (event) => {
                        const maxIndex = links.length - 1;
                        const nextIndex = i === maxIndex ? 0 : clamp(i + 1, maxIndex);
                        const prevIndex = i === 0 ? maxIndex : clamp(i - 1, maxIndex);
                        switch (event.key) {
                            case 'ArrowDown':
                            case 'Enter':
                                event.preventDefault();
                                event.stopPropagation();
                                setActive(i);
                                break;
                            case 'Escape':
                            case 'ArrowUp':
                                event.preventDefault();
                                event.stopPropagation();
                                setActive(-1);
                                break;
                            case 'ArrowRight':
                                event.preventDefault();
                                event.stopPropagation();
                                navItemRef.current[nextIndex]?.focus();
                                break;
                            case 'ArrowLeft':
                                event.preventDefault();
                                event.stopPropagation();
                                navItemRef.current[prevIndex]?.focus();
                                break;
                            case 'Home':
                                event.preventDefault();
                                event.stopPropagation();
                                navItemRef.current[0]?.focus();
                                break;
                            case 'End':
                                event.preventDefault();
                                event.stopPropagation();
                                navItemRef.current[maxIndex]?.focus();
                                break;
                            default:
                                break;
                        }
                    } },
                    link.text,
                    " ",
                    link.children && React.createElement(SvgChevron, null)),
                link.children && (React.createElement(S.NavDropdown, { "data-has-featured": !!link.featured, "data-bg-color": link.baseColour, "data-align": checkGap(navItemRef.current[i], link) },
                    React.createElement(S.NavDropDownInner, { "data-has-child": link.children.some(child => child.children && child.children.length > 0), onMouseLeave: () => setThirdLvlItem('') },
                        link.featured && link.featured.length > 0 &&
                            React.createElement(S.NavFeaturedLinks, null, link.featured.map((featured) => (React.createElement(S.NavFeaturedDropdownItem, { key: featured.id },
                                React.createElement(S.NavDropdownLink, { href: featured.url, onKeyDown: (event) => handleKeyPress(event, i) },
                                    React.createElement(S.NavLinkInner, null,
                                        React.createElement(S.FeaturedLinkText, null, featured.text),
                                        React.createElement(S.FeaturedLinkSummary, null, featured.summary)),
                                    React.createElement(S.CircleArrow, { "data-border-color": featured.brandColor },
                                        React.createElement(SvgArrow, null))))))),
                        React.createElement(S.NavNormalLinks, null, link.children.map((child, i) => (React.createElement(S.NavDropdownItem, { key: child.id, "data-set-active": thirdLvlItem === child.id, onMouseEnter: () => setThirdLvlItem(child.id) },
                            React.createElement(S.NavDropdownLink, { href: child.url, onKeyDown: (event) => {
                                    handleKeyPress(event, i),
                                        handleThirdLvlPress(event, i, child.id);
                                } },
                                React.createElement(S.DropDownText, null,
                                    child.text,
                                    " ",
                                    React.createElement(SvgArrow, null)),
                                React.createElement(S.DropDownSummary, null, child.summary),
                                translations && React.createElement(S.SrHasChild, null, translations['global.header.haschild'] ?? '')))))),
                        link.children.some(child => child.children && child.children.length > 0) && (React.createElement(S.ThirdLevelChild, null, link.children.map((child) => (child.children && (React.createElement(S.NavChildDropdown, { key: child.id, "data-show-third-level": thirdLvlItem === child.id }, child.children.map((subChild, i) => (React.createElement(S.NavDropdownItem, { key: subChild.id },
                            React.createElement(S.NavDropdownLink, { ref: (el) => (navItemThirdLvlRef.current[i] = el), href: subChild.url, onKeyDown: (event) => handleKeyPress(event, i) },
                                React.createElement(S.DropDownText, null,
                                    subChild.text,
                                    " ",
                                    React.createElement(SvgArrow, null)))))))))))))))))),
            React.createElement(S.ScrolledSearchArea, null,
                React.createElement(S.SearchBoxContainer, { id: "nav-search" },
                    React.createElement(S.SearchBox, null,
                        React.createElement(S.SearchInput, { type: "search", id: "search", placeholder: "Search", value: searchTerm, onChange: (event) => {
                                setSearchTerm(event.target.value);
                            } }),
                        React.createElement(S.SearchButton, { type: "submit", "aria-label": "Search", onClick: handleRedirect }, getSystemIcon(SystemIcon.Search))))))));
    function handleKeyPress(event, index) {
        switch (event.key) {
            case 'Escape':
            case 'ArrowUp':
                event.preventDefault();
                event.stopPropagation();
                setActive(-1);
                navItemRef.current[index]?.focus();
                break;
            default:
                break;
        }
    }
    function handleThirdLvlPress(event, index, id) {
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                event.stopPropagation();
                setThirdLvlItem(id);
                setTimeout(() => {
                    navItemThirdLvlRef.current[index]?.focus();
                }, 100);
                break;
            default:
                break;
        }
    }
};
const Header = (props) => {
    const mobileLayout = useMedia([until(Device.Desktop)], [true], false);
    const [showNav, setShowNav] = React.useState(false);
    const [hasScrolled, setHasScrolled] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [activeMenu, setActiveMenu] = React.useState(-1);
    const handleScroll = () => {
        const position = window.scrollY;
        position > 30 ? setHasScrolled(true) : setHasScrolled(false);
    };
    React.useEffect(() => {
        handleScroll();
    }, []);
    React.useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [hasScrolled]);
    return (React.createElement(React.Fragment, null,
        React.createElement(S.GlobalStyles, null),
        React.createElement(SkipLink, { type: 'header', translations: props.translations }),
        React.createElement(S.Container, { "data-is-sticky": hasScrolled },
            props.usp && React.createElement(UspBar, { ...props.usp, id: 'headerUsp', translations: props.translations }),
            React.createElement(S.Inner, null,
                React.createElement(S.LogoWrapper, null,
                    React.createElement(S.LogoLink, { href: "/", "aria-label": props.translations?.['global.navigation.home'] },
                        React.createElement(SvgLogo, null))),
                React.createElement(S.TopArea, { "data-indent": props.indentSecondLogo ?? false },
                    React.createElement(S.TopGrid, { "data-menu-open": showNav },
                        React.createElement(S.SecondaryLogo, null,
                            React.createElement(S.LogoLink, { href: "/" },
                                React.createElement(S.SecondaryLogoImg, { src: "/img/BSA-logo-edit.png", alt: "Secondary Logo" })),
                            React.createElement(S.LogoSubText, null, props.logoSubText)),
                        React.createElement(S.SearchBoxContainer, { id: "nav-search" },
                            React.createElement(S.SearchBox, null,
                                React.createElement(S.SearchInput, { type: "search", id: "search", placeholder: "Search", value: searchTerm, onChange: (event) => {
                                        setSearchTerm(event.target.value);
                                    } }),
                                React.createElement(S.SearchButton, { type: "submit", "aria-label": "Search", onClick: handleRedirect }, getSystemIcon(SystemIcon.Search)))),
                        React.createElement(S.CtaArea, null,
                            props.headerCta &&
                                React.createElement(LinkButton, { buttonType: props.headerCta.brandColor ?? 'orange', href: props.headerCta.url, target: props.headerCta.target, icon: mobileLayout ? props.mobileHeaderCtaIcon : SystemIcon.ArrowRightThin, iconOnly: mobileLayout }, !mobileLayout && props.headerCta.text),
                            React.createElement(S.MobileSearchButton, { type: "button", onClick: () => setShowNav(!showNav) }, getSystemIcon(SystemIcon.Search))),
                        React.createElement(S.BurgerBtn, { type: "button", onClick: () => setShowNav(!showNav) },
                            React.createElement("div", null, getSystemIcon(showNav ? SystemIcon.Close : SystemIcon.Burger))))),
                React.createElement(S.MainNav, null,
                    React.createElement(DesktopNavigation, { active: activeMenu, links: props.navigationLinks, searchTerm: searchTerm, setSearchTerm: (searchTerm) => setSearchTerm(searchTerm), handleRedirect: handleRedirect, setActive: setActiveMenu })),
                React.createElement(MobileNavigation, { links: props.navigationLinks, isOpen: showNav, headerCta: props.headerCta, setShowNav: setShowNav, searchTerm: searchTerm, setSearchTerm: (searchTerm) => setSearchTerm(searchTerm), handleRedirect: handleRedirect })))));
    function handleRedirect(event) {
        event.preventDefault();
        if (!searchTerm || searchTerm.length === 0 || props.searchResultsUrl === null) {
            return;
        }
        window.location.href = `${props.searchResultsUrl}?searchTerm=${searchTerm}`;
    }
};
export default Header;
